import { AfterViewInit, Component, Inject, OnDestroy, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FileHelperService } from '../../services/file-helper.service';
import { IAttachmentDialogData } from '@app/lib/models/dialog-data.model';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxExtendedPdfViewerModule, PagesLoadedEvent } from 'ngx-extended-pdf-viewer'; // Import PagesLoadedEvent

@Component({
  selector: 'app-attachment-viewer',
  standalone: true,
  imports: [
    CommonModule,
    NgxExtendedPdfViewerModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './attachment-viewer.component.html',
  styleUrls: ['./attachment-viewer.component.scss'],
})
export class AttachmentViewerComponent implements AfterViewInit, OnDestroy {

  pdfSrc: string | null = null;
  imageSrc: string | null = null;

  loading: WritableSignal<boolean> = signal(true); // Start true by default
  
  // View State
  zoomLevel = 1;
  rotation = 0;

  constructor(
    private fileService: FileHelperService,
    public dialogRef: MatDialogRef<AttachmentViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAttachmentDialogData
  ) {}

  ngAfterViewInit(): void {
    this.loadAttachment();
  }

  private loadAttachment() {
    this.loading.set(true);

    this.fileService.getBlobData(this.data.path, this.data.queryParams).subscribe({
      next: async (blob) => {
        if (await this.isPdf(blob)) {
          this.imageSrc = null;
          this.pdfSrc = URL.createObjectURL(blob);
          // NOTE: Do NOT set loading false here. Wait for (pagesLoaded).
        } else if (await this.isImage(blob)) {
          this.pdfSrc = null;
          this.imageSrc = URL.createObjectURL(blob);
          // NOTE: Do NOT set loading false here. Wait for (load).
        } else {
          // Unsupported type
          this.pdfSrc = null;
          this.imageSrc = null;
          this.loading.set(false); // Stop loading if we can't show it
        }
      },
      error: (err) => {
        console.error('Error loading attachment', err);
        this.loading.set(false);
      }
    });
  }

  // --- Type Checking Logic ---
  private async isPdf(blob: Blob) {
    const header = await this.readHeader(blob, 4);
    return header === '%PDF' || blob.type === 'application/pdf';
  }

  private async isImage(blob: Blob) {
    return blob.type.startsWith('image/');
  }

  private async readHeader(blob: Blob, len: number): Promise<string> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const arr = new Uint8Array(reader.result as ArrayBuffer);
        resolve(String.fromCharCode(...arr));
      };
      reader.readAsArrayBuffer(blob.slice(0, len));
    });
  }

  // --- Load Event Handlers ---

  /**
   * Triggered by ngx-extended-pdf-viewer when the document is visually ready.
   */
  onPdfPageLoaded(event: PagesLoadedEvent) {
    this.loading.set(false);
  }

  /**
   * Triggered by the <img> tag when the resource is rendered.
   */
  onImageLoaded() {
    this.loading.set(false);
  }

  /**
   * Fallback if loading fails specifically for the resource
   */
  onErrorLoading() {
     this.loading.set(false);
  }

  // --- Image Controls ---
  zoomIn() { 
    this.zoomLevel = parseFloat((this.zoomLevel + 0.1).toFixed(1)); 
  }
  
  zoomOut() { 
    if (this.zoomLevel > 0.2) {
      this.zoomLevel = parseFloat((this.zoomLevel - 0.1).toFixed(1));
    }
  }

  rotateLeft() { this.rotation -= 90; }
  rotateRight() { this.rotation += 90; }

  fitToScreen() { 
    this.zoomLevel = 1; 
    this.rotation = 0;
  }

  downloadImage() {
    if (!this.imageSrc) return;
    const link = document.createElement('a');
    link.href = this.imageSrc;
    link.download = `document-${new Date().getTime()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  printImage() {
    if (!this.imageSrc) return;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(`
        <html>
          <head><title>Print Image</title></head>
          <body style="margin:0; display:flex; justify-content:center; align-items:center; height: 100vh;">
            <img src="${this.imageSrc}" style="max-width:100%; max-height:100%;">
            <script>
              window.onload = () => { window.print(); window.close(); };
            </script>
          </body>
        </html>
      `);
      win.document.close();
    }
  }

  closeDialog() { 
    this.dialogRef.close(); 
  }

  ngOnDestroy() {
    if (this.pdfSrc) URL.revokeObjectURL(this.pdfSrc);
    if (this.imageSrc) URL.revokeObjectURL(this.imageSrc);
  }
}